import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Textarea, Button, Select, Switch, Badge, Text, Group, Stack, Loader, ActionIcon, Box, Modal } from '@mantine/core';
import { Trash } from '@phosphor-icons/react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { showToast } from './Toast';

const KINDS = ['industry', 'rule', 'decision'];
const MAX_LEN = 1000;

export default function KnowledgePanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Agencja: wpis bez kampanii trafia do kampanii WSZYSTKICH klientów agencji.
  const isAgency = !!user?.organization?.is_agency;
  const wholeScopeLabel = isAgency ? t('settings.knowledge.wholeCompanyAgency') : t('settings.knowledge.wholeCompany');
  const [entries, setEntries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('industry');
  const [campaign, setCampaign] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ads/knowledge/');
      setEntries(Array.isArray(res.data) ? res.data : (res.data?.results || []));
    } catch {
      showToast(t('settings.knowledge.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/ads/my-campaigns/')
      .then(res => setCampaigns(res.data.campaigns || []))
      .catch(() => {});
  }, []);

  const handleAdd = async () => {
    const value = text.trim();
    if (!value) return;
    setSaving(true);
    try {
      await api.post('/ads/knowledge/', { kind, text: value, ...(campaign ? { campaign } : {}) });
      setText('');
      await load();
    } catch {
      showToast(t('settings.knowledge.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (entry) => {
    try {
      await api.patch(`/ads/knowledge/${entry.id}/`, { is_active: !entry.is_active });
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, is_active: !e.is_active } : e));
    } catch {
      showToast(t('settings.knowledge.saveError'), 'error');
    }
  };

  const handleDelete = async () => {
    const entry = toDelete;
    if (!entry) return;
    setDeleting(true);
    try {
      await api.delete(`/ads/knowledge/${entry.id}/`);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
      setToDelete(null);
    } catch {
      showToast(t('settings.knowledge.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const kindLabel = (k) => t(`settings.knowledge.kinds.${k}`, { defaultValue: k });

  return (
    <Stack gap="md" maw={960}>
      <Box>
        <Text size="sm" fw={600}>{t('settings.knowledge.title')}</Text>
        <Text size="xs" c="dimmed">{t('settings.knowledge.subtitle')}</Text>
      </Box>

      <Stack gap="xs" maw={640}>
        <Group gap="sm" grow align="flex-end">
          <Select
            size="xs"
            label={t('settings.knowledge.kindLabel')}
            value={kind}
            onChange={(v) => v && setKind(v)}
            data={KINDS.map(k => ({ value: k, label: kindLabel(k) }))}
            allowDeselect={false}
          />
          <Select
            size="xs"
            label={t('settings.knowledge.campaignLabel')}
            placeholder={wholeScopeLabel}
            value={campaign}
            onChange={setCampaign}
            data={campaigns.map(c => ({ value: String(c.id), label: c.name }))}
            clearable
            searchable
          />
        </Group>
        {isAgency && (
          <Text size="xs" c="dimmed">{t('settings.knowledge.agencyScopeHint')}</Text>
        )}
        <Textarea
          size="xs"
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          maxLength={MAX_LEN}
          minRows={3}
          autosize
          placeholder={t('settings.knowledge.textPlaceholder')}
          description={t('settings.knowledge.hints')}
        />
        <Group justify="space-between">
          <Text size="xs" c="dimmed">{text.length}/{MAX_LEN}</Text>
          <Button color="brand" size="xs" onClick={handleAdd} loading={saving} disabled={!text.trim()}>
            {t('settings.knowledge.add')}
          </Button>
        </Group>
      </Stack>

      {loading ? (
        <Loader size="sm" color="brand" />
      ) : entries.length === 0 ? (
        <Text size="sm" c="dimmed">{t('settings.knowledge.empty')}</Text>
      ) : (
        <Table verticalSpacing="xs" fz="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('settings.knowledge.cols.kind')}</Table.Th>
              <Table.Th>{t('settings.knowledge.cols.text')}</Table.Th>
              <Table.Th>{t('settings.knowledge.cols.campaign')}</Table.Th>
              <Table.Th>{t('settings.knowledge.cols.source')}</Table.Th>
              <Table.Th>{t('settings.knowledge.cols.active')}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {entries.map(e => (
              <Table.Tr key={e.id} style={{ opacity: e.is_active ? 1 : 0.55 }}>
                <Table.Td><Badge variant="light" color="brand" size="sm">{kindLabel(e.kind)}</Badge></Table.Td>
                <Table.Td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.text}</Table.Td>
                <Table.Td>{e.campaign_name || wholeScopeLabel}</Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{t(`settings.knowledge.sources.${e.source}`, { defaultValue: '' })}</Text>
                </Table.Td>
                <Table.Td>
                  <Switch size="xs" color="brand" checked={e.is_active} onChange={() => handleToggle(e)} aria-label={t('settings.knowledge.cols.active')} />
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={() => setToDelete(e)} aria-label={t('settings.knowledge.delete')}>
                    <Trash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={!!toDelete}
        onClose={() => !deleting && setToDelete(null)}
        title={t('settings.knowledge.deleteConfirmTitle')}
        centered
        size="sm"
      >
        <Stack gap="sm">
          <Text size="sm">{t('settings.knowledge.deleteConfirmText')}</Text>
          {toDelete?.text && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} lineClamp={4}>
              {toDelete.text}
            </Text>
          )}
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="default" onClick={() => setToDelete(null)} disabled={deleting}>
              {t('settings.knowledge.cancel')}
            </Button>
            <Button size="xs" color="red" onClick={handleDelete} loading={deleting}>
              {t('settings.knowledge.deleteConfirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
